import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { BlockVisualization } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';

import type { NewBlockData } from 'webapp/services/model-manager/blocks';

export default class BlockPopupAiSummaryAutocreateComponent extends PopupInnerModalComponent<PopupData.Block.AISummaryAutoCreate> {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.userSettingsService.hasSeenAiSummaryAutocreatePopup = true;
	}

	get board() {
		return this.popupData.board;
	}

	get activeDatawall() {
		return this.popupData.activeDatawall;
	}

	@action
	create() {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;
		const layer = this.infolayerService.loading('Creating AI summary...');

		const blockData: NewBlockData = {
			visualization: BlockVisualization.AI_SUMMARY,
			fromMetricDateRange: this.board.commonBlockDateRange?.formula,
		};

		this.mmBlocksService
			.addBlockToBoardInDesigner(this.board, blockData, undefined, this.activeDatawall)
			.then((blockRecord) => {
				const block = this.mmBlocksService.findModel(blockRecord.id!)!;
				if (block) {
					const visualization = block.blockSetup.visualization as VisualizationAiSummary;
					return visualization.addBoardMetricsToBlock().then(() => {
						layer.ok('AI summary created!');
						this.eventManagerService.trigger(AppEvent.BLOCK_SELECT);
						this.navigationService.transitionToRoute(
							'designer.board.block',
							blockRecord,
						);
						this.submitInProgress = false;
						this.close();
					});
				}
				return null;
			})
			.catch(() => {
				this.submitInProgress = false;
				layer.close();
			});
	}
	<template>
		<div class="content">
			<img
				src="https://cdn1.databox.com/images/other/ai-summary-block-promo.png"
				height="200"
				alt="AI summary block"
			/>
			<h3>Add an AI-powered summary of your performance</h3>
			<p>
				The AI performance summary automatically generates a written summary of up to 10
				metrics, making it easier for stakeholders to understand your data.
			</p>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="No, thanks"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Add AI performance summary"
						@onTrigger={{this.create}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
